/**
 * Custom listbox for the planner.
 *
 * A native <select> can be styled shut but not open — the popup is drawn by the
 * OS. So the popup is a real element instead, portalled to <body> and
 * positioned fixed rather than absolutely inside the card: a field near the
 * bottom of a scrolling panel would otherwise have its popup clipped. It flips
 * above the trigger when there is not room below.
 *
 * Keyboard behaviour follows the ARIA listbox pattern — arrows, Home/End,
 * Enter/Space, Escape and type-ahead — because this replaces a control that had
 * all of that for free.
 *
 * Ported from the user platform's PortfolioPlanner/PlannerSelect.tsx. The
 * changes are React 17's lack of `useId`, MUI icons in place of iconsax, and
 * MUI's own `Portal` rather than react-dom's `createPortal` — this project has
 * no @types/react-dom, and MUI is already a dependency that ships its types.
 */

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Portal from "@mui/material/Portal";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckIcon from "@mui/icons-material/Check";
import { useAutoId } from "./useAutoId";
import styles from "./portfolio-planner.module.css";

const MAX_POPUP_HEIGHT = 268;
const GAP = 6;

interface Placement {
  left: number;
  width: number;
  top?: number;
  bottom?: number;
}

export function PlannerSelect<T extends string | number>({
  value,
  options,
  onChange,
  formatOption,
  labelledBy,
  disabled,
}: {
  value: T;
  options: ReadonlyArray<T>;
  onChange: (value: T) => void;
  formatOption?: (option: T) => string;
  labelledBy?: string;
  disabled?: boolean;
}) {
  const listId = useAutoId("planner-listbox");
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState<Placement | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const typeAhead = useRef({ buffer: "", at: 0 });

  const label = useCallback(
    (option: T) => (formatOption ? formatOption(option) : String(option)),
    [formatOption]
  );
  const selectedIndex = useMemo(
    () => Math.max(0, options.findIndex((option) => option === value)),
    [options, value]
  );

  /* Position against the trigger, flipping up when the space below is short. */
  const reposition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const below = window.innerHeight - rect.bottom - GAP;
    const above = rect.top - GAP;
    const next: Placement = { left: rect.left, width: rect.width };
    if (below < Math.min(MAX_POPUP_HEIGHT, 180) && above > below) {
      next.bottom = window.innerHeight - rect.top + GAP;
    } else {
      next.top = rect.bottom + GAP;
    }
    setPlacement(next);
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    reposition();
    // Capture phase so a scroll on any ancestor is caught, not just the window.
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open, reposition]);

  useEffect(() => {
    if (open) setActiveIndex(selectedIndex);
  }, [open, selectedIndex]);

  /* Keep the highlighted row in view while arrowing through a long list. */
  useEffect(() => {
    if (!open) return;
    const node = popupRef.current
      ? popupRef.current.querySelector(`[data-index="${activeIndex}"]`)
      : null;
    if (node && (node as HTMLElement).scrollIntoView) {
      (node as HTMLElement).scrollIntoView({ block: "nearest" });
    }
  }, [open, activeIndex]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        !(popupRef.current && popupRef.current.contains(target)) &&
        !(triggerRef.current && triggerRef.current.contains(target))
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  const commit = (index: number) => {
    const option = options[index];
    if (option !== undefined) onChange(option);
    setOpen(false);
    if (triggerRef.current) triggerRef.current.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (disabled) return;
    if (!open) {
      if (["Enter", " ", "ArrowDown", "ArrowUp"].indexOf(event.key) >= 0) {
        event.preventDefault();
        setOpen(true);
      }
      return;
    }
    switch (event.key) {
      case "Escape":
        event.preventDefault();
        setOpen(false);
        if (triggerRef.current) triggerRef.current.focus();
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        commit(activeIndex);
        break;
      case "ArrowDown":
        event.preventDefault();
        setActiveIndex((index) => Math.min(options.length - 1, index + 1));
        break;
      case "ArrowUp":
        event.preventDefault();
        setActiveIndex((index) => Math.max(0, index - 1));
        break;
      case "Home":
        event.preventDefault();
        setActiveIndex(0);
        break;
      case "End":
        event.preventDefault();
        setActiveIndex(options.length - 1);
        break;
      case "Tab":
        setOpen(false);
        break;
      default: {
        if (event.key.length !== 1) break;
        const now = Date.now();
        const state = typeAhead.current;
        state.buffer =
          now - state.at > 700 ? event.key : state.buffer + event.key;
        state.at = now;
        const match = options.findIndex(
          (option) =>
            label(option).toLowerCase().indexOf(state.buffer.toLowerCase()) === 0
        );
        if (match >= 0) setActiveIndex(match);
      }
    }
  };

  const popup =
    open && placement ? (
      <Portal container={document.body}>
        <div
            ref={popupRef}
            className={styles.selectPopup}
            style={{
              left: placement.left,
              width: placement.width,
              ...(placement.top != null
                ? { top: placement.top }
                : { bottom: placement.bottom }),
            }}
          >
            <ul
              className={styles.selectList}
              id={listId}
              role="listbox"
              aria-labelledby={labelledBy}
              tabIndex={-1}
            >
              {options.map((option, index) => {
                const isSelected = option === value;
                return (
                  <li key={String(option)}>
                    <button
                      type="button"
                      data-index={index}
                      role="option"
                      aria-selected={isSelected}
                      className={`${styles.selectOption} ${
                        index === activeIndex ? styles.selectOptionActive : ""
                      } ${isSelected ? styles.selectOptionSelected : ""}`}
                      onMouseEnter={() => setActiveIndex(index)}
                      onClick={() => commit(index)}
                    >
                      <span>{label(option)}</span>
                      {isSelected ? (
                        <CheckIcon style={{ fontSize: 15 }} aria-hidden="true" />
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
        </div>
      </Portal>
    ) : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        className={`${styles.selectTrigger} ${open ? styles.selectTriggerOpen : ""}`}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={open ? listId : undefined}
        aria-labelledby={labelledBy}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={onKeyDown}
      >
        <span className={styles.selectValue}>{label(value)}</span>
        <ExpandMoreIcon className={styles.selectChevron} aria-hidden="true" />
      </button>
      {popup}
    </>
  );
}

export default PlannerSelect;
